import React, { useCallback, useEffect, useState } from 'react';
import { confirmAction, historyWarning } from '../../utils/confirm';
import { AlertRule, AlertCondition, AlertContact, ConditionGroup, Zone } from '../../types';
import {
    fetchAlertContacts, createAlertRule, deleteAlertRule, toggleAlertRule,
} from '../../services/api';
import {
    SurveillanceDevice, sectionStyle, btnStyle, inputStyle, labelStyle,
    OPERATORS, PROPERTY_LABELS, blankCondition,
} from './surveillanceShared';
import { useRuleForm } from './hooks/useRuleForm';
import { useDeviceProperties } from './hooks/useDeviceProperties';

interface RulesTabProps {
    rules: AlertRule[];
    setRules: React.Dispatch<React.SetStateAction<AlertRule[]>>;
    devices: SurveillanceDevice[];
    zones: Zone[];
    /** Laddar om reglerna (efter spara, ta bort, kopiera). */
    onReload: () => Promise<void>;
    /** Visar fel i sidans röda ruta. */
    onError: (message: string) => void;
}

const logicLabel = (logic: string): string => (logic === 'AND' ? 'OCH' : 'ELLER');

/** Märkning på regelkortet när regeln står stilla för att en av dess sensorer är avstängd. */
const PausedBadge: React.FC<{ rule: AlertRule; compact?: boolean }> = ({ rule, compact }) => {
    const paused = rule.enabled ? (rule.disabled_devices ?? []) : [];
    if (paused.length === 0) return null;
    const names = paused.map(d => d.name).join(', ');
    return (
        <div
            title="Regeln kontrolleras inte förrän sensorn slås på igen"
            style={{
                display: 'inline-block', marginTop: '6px',
                padding: compact ? '2px 8px' : '3px 10px', borderRadius: '12px',
                backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeeba',
                fontSize: compact ? '11px' : '12px', fontWeight: 600,
            }}
        >
            ⏸ Körs inte – {paused.length === 1 ? 'sensorn' : 'sensorerna'} {names} {paused.length === 1 ? 'är avstängd' : 'är avstängda'}
        </div>
    );
};

/** Kantfärg på regelkortet: grön = körs, gul = pausad av avstängd sensor, grå = avstängd regel. */
const ruleBorderColor = (rule: AlertRule): string => {
    if (!rule.enabled) return '#aaa';
    if ((rule.disabled_devices ?? []).length > 0) return '#ffc107';
    return '#28a745';
};

const RulesTab: React.FC<RulesTabProps> = ({ rules, setRules, devices, zones, onReload, onError }) => {
    // Kontakter används bara som mottagare här; de redigeras på sidan Kontakter.
    const [contacts, setContacts] = useState<AlertContact[]>([]);
    useEffect(() => {
        fetchAlertContacts().then(setContacts).catch(() => { /* listan visas utan mottagarnamn */ });
    }, []);

    const {
        showForm: showRuleForm, editRuleId, form: ruleForm, setForm: setRuleForm,
        openCreate: onOpenCreate, openEdit: onOpenEdit, cancel: onCancelForm, submit: onSubmitForm,
    } = useRuleForm({ onSaved: onReload, onError });

    const {
        labelMap, ensureLoaded: prefetchDeviceProperties,
        propertiesForDevice, propertyDetailsForDevice, isLoading: isPropertiesLoading,
    } = useDeviceProperties();

    // Hämta mätvärden för varje enhet som något villkor pekar på
    useEffect(() => {
        const ieees = ruleForm.condition_groups
            .flatMap(g => g.conditions)
            .map(c => c.device_ieee)
            .filter(Boolean);
        prefetchDeviceProperties(ieees);
    }, [ruleForm.condition_groups, prefetchDeviceProperties]);

    // Läsbart namn för en nyckel (t.ex. "p_62005" → "Status")
    const propLabel = useCallback((prop: string): string =>
        PROPERTY_LABELS[prop] ?? labelMap[prop] ?? prop,
    [labelMap]);

    const onToggleRule = async (id: number) => {
        try { await toggleAlertRule(id); setRules(prev => prev.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r)); }
        catch (e: any) { onError(e.message); }
    };
    const onDeleteRule = async (id: number) => {
        if (!confirmAction('Ta bort den här larmregeln?')) return;
        try {
            const result = await deleteAlertRule(id);
            if ('historyCount' in result) {
                if (!confirmAction(historyWarning(result.historyCount))) return;
                await deleteAlertRule(id, true);
            }
            await onReload();
        } catch (e: any) { onError(e.message); }
    };

    /**
     * Kopierar en regel till en annan zon och byter varje villkors enhet
     * mot den valda målenheten.
     */
    const onCopyRule = async (
        rule: AlertRule,
        targetZoneId: number,
        targetDeviceIeee: string,
        newName: string,
    ): Promise<void> => {
        // Samma grupper som listan visar (gamla platta regler blir en grupp)
        const sourceGroups: { logic: 'AND' | 'OR'; conditions: AlertCondition[] }[] =
            rule.condition_groups && rule.condition_groups.length > 0
                ? rule.condition_groups
                : [{
                    logic: rule.condition_logic ?? 'AND',
                    conditions: rule.conditions && rule.conditions.length > 0
                        ? rule.conditions
                        : (rule.devices ?? []).flatMap(d =>
                            (rule.properties ?? (rule.property ? [rule.property] : [])).map(p => ({
                                device_ieee: d.ieee_address, property: p,
                                operator: rule.operator, threshold: rule.threshold,
                            } as AlertCondition))
                          ),
                }];
        const newGroups = sourceGroups.map(g => ({
            logic: g.logic,
            conditions: g.conditions.map(c => ({ ...c, device_ieee: targetDeviceIeee })),
        }));
        const flatConditions = newGroups.flatMap(g => g.conditions);
        const payload = {
            name: newName,
            zone_id: targetZoneId,
            group_name: rule.group_name ?? null,
            condition_groups: newGroups,
            group_logic: rule.group_logic ?? 'OR',
            conditions: flatConditions,
            condition_logic: newGroups.length === 1 ? newGroups[0].logic : (rule.group_logic ?? 'OR'),
            cooldown_minutes: rule.cooldown_minutes,
            trigger_threshold: rule.trigger_threshold ?? 1,
            trigger_mode: rule.trigger_mode ?? 'count',
            contact_ids: rule.contacts.map(c => c.id),
        };
        try {
            await createAlertRule(payload);
            await onReload();
        } catch (e: any) { onError(e.message); throw e; }
    };

    // Per-condition zone filter — keyed by "groupIdx-condIdx" (local UI state only)
    const [conditionZones, setConditionZones] = useState<Record<string, string>>({});
    // Collapsed zone groups (by zone id, or 'unassigned')
    const [collapsedZones, setCollapsedZones] = useState<Set<string>>(new Set());
    // Zoner utan regler ligger ihopdragna i botten av varje del (område eller "utan område"),
    // så listan inte blir lång när zonerna är många. Nyckel = delens nyckel.
    const [openEmptySections, setOpenEmptySections] = useState<Set<string>>(new Set());
    // Group filter ('' = all)
    const [groupFilter, setGroupFilter] = useState<string>('');
    // Copy dialog state — null when closed
    const [copyDialog, setCopyDialog] = useState<{
        rule: AlertRule;
        targetZoneId: string;
        targetDeviceIeee: string;
        newName: string;
        submitting: boolean;
        warning: string | null;
    } | null>(null);
    const setConditionZone = (groupIdx: number, condIdx: number, zoneId: string) => {
        setConditionZones(prev => ({ ...prev, [`${groupIdx}-${condIdx}`]: zoneId }));
        updateCondition(groupIdx, condIdx, { device_ieee: '', property: '' });
    };

    // Pre-warm device properties when copy dialog opens or its target zone changes,
    // so the property select / missing-warning has data ready instead of
    // showing a stale fallback or false-positive while one-by-one fetches finish.
    useEffect(() => {
        if (!copyDialog) return;
        const zone = zones.find(z => String(z.id) === copyDialog.targetZoneId);
        if (!zone) return;
        prefetchDeviceProperties(zone.devices.map(d => d.ieee_address));
    }, [copyDialog?.targetZoneId, copyDialog?.rule.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateCondition = (groupIdx: number, condIdx: number, patch: Partial<AlertCondition>) => {
        setRuleForm(prev => ({
            ...prev,
            condition_groups: prev.condition_groups.map((g, gi) =>
                gi === groupIdx
                    ? { ...g, conditions: g.conditions.map((c, ci) => ci === condIdx ? { ...c, ...patch } : c) }
                    : g
            ),
        }));
    };
    const addCondition = (groupIdx: number) => setRuleForm(prev => ({
        ...prev,
        condition_groups: prev.condition_groups.map((g, gi) =>
            gi === groupIdx ? { ...g, conditions: [...g.conditions, blankCondition()] } : g
        ),
    }));
    const removeCondition = (groupIdx: number, condIdx: number) => setRuleForm(prev => ({
        ...prev,
        condition_groups: prev.condition_groups.map((g, gi) =>
            gi === groupIdx ? { ...g, conditions: g.conditions.filter((_, ci) => ci !== condIdx) } : g
        ),
    }));
    const addGroup = () => setRuleForm(prev => ({
        ...prev,
        condition_groups: [...prev.condition_groups, { logic: 'AND' as const, conditions: [blankCondition()] }],
    }));
    const removeGroup = (groupIdx: number) => setRuleForm(prev => ({
        ...prev,
        condition_groups: prev.condition_groups.filter((_, gi) => gi !== groupIdx),
    }));
    const updateGroupLogic = (groupIdx: number, logic: 'AND' | 'OR') => setRuleForm(prev => ({
        ...prev,
        condition_groups: prev.condition_groups.map((g, gi) => gi === groupIdx ? { ...g, logic } : g),
    }));

    const ruleFormPanel = (
                <div style={{ ...sectionStyle, borderColor: '#007bff' }}>
                    <h3 style={{ marginTop: 0 }}>{editRuleId ? 'Ändra regel' : 'Ny larmregel'}</h3>
                    <form onSubmit={onSubmitForm}>
                        {/* Zone + Group */}
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 220px', maxWidth: '400px' }}>
                                <label style={labelStyle}>Zon</label>
                                <select
                                    style={inputStyle}
                                    value={ruleForm.zone_id == null ? '' : String(ruleForm.zone_id)}
                                    onChange={e => setRuleForm(p => ({
                                        ...p,
                                        zone_id: e.target.value === '' ? null : Number(e.target.value),
                                    }))}
                                >
                                    <option value="">— Ingen zon —</option>
                                    {zones.map(z => (
                                        <option key={z.id} value={String(z.id)}>{z.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ flex: '1 1 220px', maxWidth: '400px' }}>
                                <label style={labelStyle}>Grupp</label>
                                <input
                                    style={inputStyle}
                                    list="alert-rule-groups"
                                    value={ruleForm.group_name}
                                    placeholder="t.ex. Frysar, Dörrar, El"
                                    maxLength={100}
                                    onChange={e => setRuleForm(p => ({ ...p, group_name: e.target.value }))}
                                />
                                <datalist id="alert-rule-groups">
                                    {Array.from(new Set(rules.map(r => r.group_name).filter((g): g is string => !!g && g.trim() !== '')))
                                        .sort((a, b) => a.localeCompare(b))
                                        .map(g => <option key={g} value={g} />)}
                                </datalist>
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    Frivillig etikett som gör det lättare att filtrera reglerna i listan nedanför.
                                </div>
                            </div>
                        </div>

                        {/* Rule name */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Regelns namn</label>
                            <input style={{ ...inputStyle, maxWidth: '400px' }} value={ruleForm.name} required placeholder="t.ex. Hög temperatur"
                                onChange={e => setRuleForm(p => ({ ...p, name: e.target.value }))} />
                        </div>

                        {/* All Devices toggle */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
                                <input type="checkbox" checked={ruleForm.applies_to_all_devices}
                                    onChange={e => {
                                        const allDevices = e.target.checked;
                                        setRuleForm(prev => ({
                                            ...prev,
                                            applies_to_all_devices: allDevices,
                                            condition_groups: prev.condition_groups.map(g => ({
                                                ...g,
                                                conditions: g.conditions.map(c => ({
                                                    ...c,
                                                    device_ieee: allDevices ? '*' : '',
                                                })),
                                            })),
                                        }));
                                    }} />
                                Gäller alla enheter
                            </label>
                            {ruleForm.applies_to_all_devices && (
                                <div style={{ fontSize: '12px', color: '#555', marginTop: '4px', paddingLeft: '24px' }}>
                                    Regeln körs för varje enhet som skickar in värden. Villkoren jämförs mot enhetens egna värden.
                                </div>
                            )}
                        </div>

                        {/* Conditions builder */}
                        <div style={{ marginBottom: '16px' }}>
                            {/* Top-level group join logic — only shown when there are multiple groups */}
                            {ruleForm.condition_groups.length > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '8px 12px', backgroundColor: '#e8f0fe', borderRadius: '6px', border: '1px solid #c5d5f7' }}>
                                    <label style={{ ...labelStyle, marginBottom: 0 }}>Grupperna kopplas med:</label>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        {(['OR', 'AND'] as const).map(logic => (
                                            <button key={logic} type="button"
                                                onClick={() => setRuleForm(p => ({ ...p, group_logic: logic }))}
                                                style={{
                                                    ...btnStyle(ruleForm.group_logic === logic ? '#007bff' : '#e9ecef',
                                                        ruleForm.group_logic === logic ? 'white' : '#333'),
                                                    padding: '4px 14px', fontSize: '12px', fontWeight: 700,
                                                }}>
                                                {logicLabel(logic)}
                                            </button>
                                        ))}
                                    </div>
                                    <span style={{ fontSize: '12px', color: '#555' }}>
                                        {ruleForm.group_logic === 'AND' ? 'Alla grupper måste slå till' : 'Det räcker att en grupp slår till'}
                                    </span>
                                </div>
                            )}

                            {ruleForm.condition_groups.map((group, groupIdx) => (
                                <div key={groupIdx}>
                                    {groupIdx > 0 && (
                                        <div style={{ textAlign: 'center', margin: '8px 0', fontSize: '12px', fontWeight: 700, color: '#007bff', letterSpacing: '1px' }}>
                                            — {logicLabel(ruleForm.group_logic)} —
                                        </div>
                                    )}
                                    <div style={{
                                        border: '1.5px solid #a0bfff', borderRadius: '8px',
                                        padding: '12px', marginBottom: '4px',
                                        backgroundColor: '#f5f8ff',
                                    }}>
                                        {/* Group header */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                                            {ruleForm.condition_groups.length > 1 && (
                                                <span style={{ fontSize: '13px', fontWeight: 700, color: '#0047cc', minWidth: '60px' }}>
                                                    Grupp {groupIdx + 1}
                                                </span>
                                            )}
                                            <span style={{ fontSize: '12px', color: '#555' }}>villkoren kopplas med:</span>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                {(['AND', 'OR'] as const).map(logic => (
                                                    <button key={logic} type="button"
                                                        onClick={() => updateGroupLogic(groupIdx, logic)}
                                                        style={{
                                                            ...btnStyle(group.logic === logic ? '#0056b3' : '#e9ecef',
                                                                group.logic === logic ? 'white' : '#333'),
                                                            padding: '3px 12px', fontSize: '12px', fontWeight: 700,
                                                        }}>
                                                        {logicLabel(logic)}
                                                    </button>
                                                ))}
                                            </div>
                                            <span style={{ fontSize: '11px', color: '#888' }}>
                                                {group.logic === 'AND' ? 'alla måste stämma' : 'det räcker att ett stämmer'}
                                            </span>
                                            {ruleForm.condition_groups.length > 1 && (
                                                <button type="button" onClick={() => removeGroup(groupIdx)}
                                                    style={{ ...btnStyle('#dc3545'), marginLeft: 'auto', padding: '3px 10px', fontSize: '12px' }}>
                                                    Ta bort gruppen
                                                </button>
                                            )}
                                        </div>

                                        {/* Conditions in this group */}
                                        {group.conditions.map((cond, condIdx) => {
                                            const condKey = `${groupIdx}-${condIdx}`;
                                            const detailsByKey = Object.fromEntries(
                                                propertyDetailsForDevice(cond.device_ieee).map(d => [d.key, d])
                                            );
                                            const selectedDetails = cond.property ? detailsByKey[cond.property] : undefined;
                                            const condZoneId = conditionZones[condKey] ?? '';
                                            const zoneDeviceIds = condZoneId
                                                ? new Set(zones.find(z => String(z.id) === condZoneId)?.devices.map(d => d.ieee_address) ?? [])
                                                : null;
                                            const filteredDevices = zoneDeviceIds
                                                ? devices.filter(d => zoneDeviceIds.has(d.id))
                                                : devices;
                                            const sortedProperties = propertiesForDevice(cond.device_ieee)
                                                .slice()
                                                .sort((a, b) => propLabel(a).localeCompare(propLabel(b)));
                                            return (
                                                <div key={condIdx}>
                                                    {condIdx > 0 && (
                                                        <div style={{ textAlign: 'center', margin: '4px 0', fontSize: '11px', fontWeight: 700, color: '#0056b3', letterSpacing: '1px' }}>
                                                            {logicLabel(group.logic)}
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        display: 'grid', gridTemplateColumns: ruleForm.applies_to_all_devices ? '2fr 1fr 1fr auto' : '1.5fr 2fr 2fr 1fr 1fr auto',
                                                        gap: '8px', alignItems: 'end', marginBottom: '6px',
                                                        padding: '10px', borderRadius: '6px',
                                                        backgroundColor: 'white', border: '1px solid #dee2e6',
                                                    }}>
                                                        {!ruleForm.applies_to_all_devices && (
                                                        <div>
                                                            <label style={{ ...labelStyle, marginBottom: '2px' }}>Zon</label>
                                                            <select style={inputStyle} value={condZoneId}
                                                                onChange={e => setConditionZone(groupIdx, condIdx, e.target.value)}>
                                                                <option value="">— alla zoner —</option>
                                                                {zones.map(z => (
                                                                    <option key={z.id} value={String(z.id)}>{z.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        )}
                                                        {!ruleForm.applies_to_all_devices && (
                                                        <div>
                                                            <label style={{ ...labelStyle, marginBottom: '2px' }}>Enhet</label>
                                                            <select style={inputStyle} value={cond.device_ieee}
                                                                onChange={e => updateCondition(groupIdx, condIdx, { device_ieee: e.target.value, property: '' })}>
                                                                <option value="">— välj enhet —</option>
                                                                {filteredDevices.map(d => (
                                                                    <option key={d.id} value={d.id}>{d.name || d.id}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        )}
                                                        <div>
                                                            <label style={{ ...labelStyle, marginBottom: '2px' }}>Mätvärde</label>
                                                            {ruleForm.applies_to_all_devices ? (
                                                                <input style={inputStyle} type="text" placeholder="t.ex. battery, online, temperature"
                                                                    value={cond.property}
                                                                    onChange={e => updateCondition(groupIdx, condIdx, { property: e.target.value, device_ieee: '*' })} />
                                                            ) : (
                                                            <select style={inputStyle} value={cond.property}
                                                                onChange={e => updateCondition(groupIdx, condIdx, { property: e.target.value })}
                                                                disabled={!cond.device_ieee}>
                                                                <option value="">
                                                                    {cond.device_ieee && isPropertiesLoading(cond.device_ieee)
                                                                        ? 'hämtar mätvärden…'
                                                                        : '— välj mätvärde —'}
                                                                </option>
                                                                {sortedProperties.map(p => {
                                                                    const d = detailsByKey[p];
                                                                    const lastVal = d?.lastValueStr ?? (d?.lastValueNum != null ? String(d.lastValueNum) : null);
                                                                    const lbl = propLabel(p);
                                                                    return (
                                                                        <option key={p} value={p}>
                                                                            {lbl !== p ? `${lbl} (${p})` : p}
                                                                            {lastVal != null ? ` — ${lastVal}${d?.unit ? ' ' + d.unit : ''}` : ''}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </select>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label style={{ ...labelStyle, marginBottom: '2px' }}>Jämförelse</label>
                                                            <select style={inputStyle} value={cond.operator}
                                                                onChange={e => updateCondition(groupIdx, condIdx, { operator: e.target.value })}>
                                                                {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.value}</option>)}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label style={{ ...labelStyle, marginBottom: '2px' }}>Gränsvärde</label>
                                                            <input style={inputStyle} type="text" placeholder="t.ex. 20 eller OFF"
                                                                value={String(cond.threshold)}
                                                                onChange={e => {
                                                                    const v = e.target.value;
                                                                    updateCondition(groupIdx, condIdx, { threshold: v === '' ? 0 : (isNaN(Number(v)) ? v : Number(v)) });
                                                                }} />
                                                        </div>
                                                        <div style={{ paddingBottom: '1px' }}>
                                                            <button type="button" onClick={() => removeCondition(groupIdx, condIdx)}
                                                                style={{ ...btnStyle('#dc3545'), padding: '7px 10px', fontSize: '14px' }}
                                                                title="Ta bort villkoret">✕</button>
                                                        </div>
                                                        {selectedDetails?.enumValues?.length ? (
                                                            <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: '4px', paddingTop: '4px' }}>
                                                                <span style={{ fontSize: '11px', color: '#888', alignSelf: 'center', marginRight: '4px' }}>Värden:</span>
                                                                {selectedDetails.enumValues.map(ev => (
                                                                    <button key={ev.value} type="button"
                                                                        onClick={() => updateCondition(groupIdx, condIdx, { threshold: isNaN(Number(ev.value)) ? ev.value : Number(ev.value) })}
                                                                        style={{
                                                                            padding: '2px 8px', fontSize: '11px',
                                                                            border: `1px solid ${String(cond.threshold) === ev.value ? '#007bff' : '#ced4da'}`,
                                                                            borderRadius: '12px', cursor: 'pointer',
                                                                            background: String(cond.threshold) === ev.value ? '#007bff' : '#f8f9fa',
                                                                            color: String(cond.threshold) === ev.value ? 'white' : '#333',
                                                                        }}>
                                                                        {ev.text} ({ev.value})
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <button type="button" onClick={() => addCondition(groupIdx)}
                                            style={{ ...btnStyle('#28a745'), fontSize: '13px', padding: '6px 14px', marginTop: '4px' }}>
                                            + Lägg till villkor
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button type="button" onClick={addGroup}
                                style={{ ...btnStyle('#6610f2'), fontSize: '13px', padding: '6px 14px', marginTop: '8px' }}>
                                + Lägg till grupp
                            </button>
                        </div>

                        {/* Cooldown + trigger threshold + trigger mode */}
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', flexWrap: 'wrap' }}>
                            <div style={{ maxWidth: '200px', flex: '1 1 180px' }}>
                                <label style={labelStyle}>Vilotid mellan larm (minuter)</label>
                                <input style={inputStyle} type="number" min={1} value={ruleForm.cooldown_minutes}
                                    onChange={e => setRuleForm(p => ({ ...p, cooldown_minutes: parseInt(e.target.value) }))} />
                            </div>
                            <div style={{ maxWidth: '200px', flex: '1 1 180px' }}>
                                <label style={labelStyle}>
                                    {ruleForm.trigger_mode === 'cooldown' ? 'Antal vilotider som krävs' : 'Antal mätningar i rad som krävs'}
                                </label>
                                <input style={inputStyle} type="number" min={1} value={ruleForm.trigger_threshold}
                                    onChange={e => setRuleForm(p => ({ ...p, trigger_threshold: Math.max(1, parseInt(e.target.value) || 1) }))} />
                            </div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Så här räknas larmet</label>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                {(['count', 'cooldown'] as const).map(mode => (
                                    <label key={mode} style={{
                                        display: 'flex', alignItems: 'flex-start', gap: '8px',
                                        padding: '8px 12px', borderRadius: '6px', cursor: 'pointer',
                                        flex: '1 1 200px',
                                        backgroundColor: ruleForm.trigger_mode === mode ? '#e8f4fd' : '#f8f9fa',
                                        border: `1px solid ${ruleForm.trigger_mode === mode ? '#0d6efd' : '#ddd'}`,
                                    }}>
                                        <input type="radio" name="trigger_mode" value={mode}
                                            checked={ruleForm.trigger_mode === mode}
                                            onChange={() => setRuleForm(p => ({ ...p, trigger_mode: mode }))}
                                            style={{ marginTop: '2px' }} />
                                        <div>
                                            <div style={{ fontWeight: 500, fontSize: '13px' }}>
                                                {mode === 'count' ? 'Antal gånger' : 'Hur länge'}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                                {mode === 'count'
                                                    ? 'Larmet går först när villkoret stämmer så många mätningar i rad. Räknaren nollställs så fort en mätning inte stämmer.'
                                                    : `Villkoret måste stämma oavbrutet i ${ruleForm.trigger_threshold * ruleForm.cooldown_minutes} minuter innan larmet går.`
                                                }
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Mottagare redigeras numera från Contacts-fliken — här visas de bara,
                            så att man ändå ser direkt om regeln inte når någon. */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Mottagare</label>
                            {(() => { const editGroups = rules.find(r => r.id === editRuleId)?.groups ?? []; return (
                            <>
                            {editGroups.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                    {editGroups.map(g => (
                                        <span key={`g${g.id}`} style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            padding: '6px 12px', borderRadius: '4px',
                                            backgroundColor: '#e8f0fe', border: '1px solid #1a4aa6', fontSize: '13px',
                                        }}>
                                            👥 {g.name} ({g.member_count} pers.)
                                        </span>
                                    ))}
                                </div>
                            )}
                            {ruleForm.contact_ids.length === 0 && editGroups.length === 0 ? (
                                <div style={{
                                    fontSize: '13px', color: '#c0392b', fontWeight: 600,
                                    padding: '8px 12px', backgroundColor: '#fdecea',
                                    border: '1px solid #f5c6cb', borderRadius: '4px',
                                }}>
                                    ⚠ Ingen mottagare — regeln larmar men når ingen. Koppla den under Kontakter.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {contacts.filter(c => ruleForm.contact_ids.includes(c.id)).map(c => (
                                        <span key={c.id} style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            padding: '6px 12px', borderRadius: '4px',
                                            backgroundColor: '#d4edda', border: '1px solid #28a745', fontSize: '13px',
                                        }}>
                                            {c.name}{c.notify_email && ' 📧'}{c.notify_sms && ' 📱'}
                                        </span>
                                    ))}
                                </div>
                            )}
                            </>
                            ); })()}
                            <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                                Ändras under Kontakter — välj kontakt eller grupp och kryssa i vilka larm de ska få.
                            </div>
                        </div>

                        {/* Action guide — shown in notifications as a link */}
                        <div style={{ marginBottom: '20px', padding: '16px', borderRadius: '8px', backgroundColor: '#f8f9fa', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#333', marginBottom: '10px' }}>
                                📋 Åtgärdsbeskrivning <span style={{ fontWeight: 400, fontSize: '12px', color: '#888' }}>(frivillig — visas i larmutskicket)</span>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={labelStyle}>Instruktion</label>
                                <textarea
                                    style={{ ...inputStyle, height: '90px', resize: 'vertical' }}
                                    placeholder="Beskriv vad mottagaren ska göra när larmet går, t.ex. ”Vrid ventilen medsols för att stänga av vattnet.”"
                                    value={ruleForm.instruction_text}
                                    onChange={e => setRuleForm(p => ({ ...p, instruction_text: e.target.value }))}
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>Bild</label>
                                {ruleForm.instruction_image ? (
                                    <div style={{ marginBottom: '8px' }}>
                                        <img
                                            src={ruleForm.instruction_image}
                                            alt="Förhandsvisning av bilden"
                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', border: '1px solid #ddd', display: 'block' }}
                                        />
                                        <button type="button"
                                            onClick={() => setRuleForm(p => ({ ...p, instruction_image: '', imageModified: true }))}
                                            style={{ ...btnStyle('#dc3545'), marginTop: '6px', fontSize: '12px', padding: '4px 10px' }}>
                                            Ta bort bilden
                                        </button>
                                    </div>
                                ) : null}
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'block', fontSize: '13px' }}
                                    onChange={e => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const reader = new FileReader();
                                        reader.onload = ev => {
                                            setRuleForm(p => ({ ...p, instruction_image: ev.target?.result as string || '', imageModified: true }));
                                        };
                                        reader.readAsDataURL(file);
                                        // reset input so same file can be re-selected after removal
                                        e.target.value = '';
                                    }}
                                />
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                                    JPEG eller PNG rekommenderas. Bilden sparas i databasen.
                                </div>
                            </div>
                        </div>

                        {/* Deviation reporting */}
                        <div style={{ marginBottom: '20px', padding: '16px', borderRadius: '8px', backgroundColor: '#f8f9fa', border: '1px solid #e2e8f0' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={!!(ruleForm as any).requires_deviation_report}
                                    onChange={e => setRuleForm(p => ({ ...p, requires_deviation_report: e.target.checked }))}
                                    style={{ width: 16, height: 16 }}
                                />
                                <span style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                                    📋 Kräver avvikelserapportering
                                </span>
                                <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>
                                    (kontakter får en länk i återhämtnings­notisen för att rapportera orsak och åtgärd)
                                </span>
                            </label>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="submit" style={btnStyle('#007bff')}>{editRuleId ? 'Spara' : 'Skapa regel'}</button>
                            <button type="button" onClick={onCancelForm} style={btnStyle('#6c757d')}>Avbryt</button>
                        </div>
                    </form>
                </div>
    );

    return (
        <div>
            {rules.length === 0 && !showRuleForm && (
                <div style={{ marginBottom: '16px' }}>
                    <button onClick={() => onOpenCreate(null)} style={btnStyle('#28a745')}>+ Ny regel</button>
                </div>
            )}

            {/* Create form is shown at the top; the edit form renders inline in the list (see below) */}
            {showRuleForm && editRuleId === null && ruleFormPanel}

            {(() => {
                // Build sorted list of distinct group names from rules
                const allGroups = Array.from(
                    new Set(rules.map(r => r.group_name).filter((g): g is string => !!g && g.trim() !== ''))
                ).sort((a, b) => a.localeCompare(b));

                // Apply group filter
                const filteredRules = groupFilter === ''
                    ? rules
                    : groupFilter === '__none__'
                        ? rules.filter(r => !r.group_name || r.group_name.trim() === '')
                        : rules.filter(r => r.group_name === groupFilter);

                // Group rules by zone_id (null → 'unassigned')
                type ZoneGroup = { key: string; zoneId: number | null; name: string; color: string; rules: AlertRule[] };
                const groupMap = new Map<string, ZoneGroup>();
                // Seed with all known zones (so empty zones still show their + button)
                for (const z of zones) {
                    groupMap.set(String(z.id), { key: String(z.id), zoneId: z.id, name: z.name, color: z.color, rules: [] });
                }
                let unassignedSeen = false;
                for (const r of filteredRules) {
                    if (r.zone_id != null) {
                        const k = String(r.zone_id);
                        if (!groupMap.has(k)) {
                            // Zone may have been deleted after the rule was created
                            groupMap.set(k, { key: k, zoneId: r.zone_id, name: `Zone #${r.zone_id}`, color: '#999', rules: [] });
                        }
                        groupMap.get(k)!.rules.push(r);
                    } else {
                        unassignedSeen = true;
                        if (!groupMap.has('unassigned')) {
                            groupMap.set('unassigned', { key: 'unassigned', zoneId: null, name: 'Ingen zon', color: '#888', rules: [] });
                        }
                        groupMap.get('unassigned')!.rules.push(r);
                    }
                }
                if (unassignedSeen && !groupMap.has('unassigned')) {
                    groupMap.set('unassigned', { key: 'unassigned', zoneId: null, name: 'Ingen zon', color: '#888', rules: [] });
                }
                // Sort: zones in their original order, Unassigned last
                const ordered: ZoneGroup[] = [
                    ...zones.map(z => groupMap.get(String(z.id))!).filter(Boolean),
                    ...Array.from(groupMap.values()).filter(g => g.zoneId != null && !zones.some(z => z.id === g.zoneId)),
                ];
                if (groupMap.has('unassigned')) ordered.push(groupMap.get('unassigned')!);

                // Delar: ett område med sina zoner, sedan zoner utan område (och "Ingen zon" sist).
                // Ett område har inga egna regler; har någon ändå lagt en regel direkt på området visas den under rubriken.
                const areaZones = zones.filter(z => z.kind === 'area').sort((a, b) => a.name.localeCompare(b.name, 'sv'));
                const zoneById = new Map(zones.map(z => [z.id, z]));
                const sections: { key: string; area: { id: number; name: string; color: string } | null; groups: ZoneGroup[]; ruleCount: number }[] = [];
                const placed = new Set<string>();
                for (const area of areaZones) {
                    const groups = ordered.filter(g => {
                        if (g.zoneId == null) return false;
                        const z = zoneById.get(g.zoneId);
                        return (z?.parent_id === area.id) || (g.zoneId === area.id && g.rules.length > 0);
                    });
                    groups.forEach(g => placed.add(g.key));
                    placed.add(String(area.id));
                    sections.push({ key: `area-${area.id}`, area: { id: area.id, name: area.name, color: area.color }, groups, ruleCount: groups.reduce((n, g) => n + g.rules.length, 0) });
                }
                const loose = ordered.filter(g => !placed.has(g.key));
                if (loose.length) sections.push({ key: 'loose', area: null, groups: loose, ruleCount: loose.reduce((n, g) => n + g.rules.length, 0) });

                // Zoner med regler först, som förut. Zoner utan regler samlas ihopdragna i botten av delen.
                const renderGroups = (list: ZoneGroup[], sectionKey: string) => {
                    const withRules = list.filter(g => g.rules.length > 0);
                    const emptyZones = list.filter(g => g.rules.length === 0);
                    const showEmpty = openEmptySections.has(sectionKey);
                    return (
                        <>
                        {withRules.map(group => {
                            const collapsed = collapsedZones.has(group.key);
                            return (
                                <div key={group.key} style={{
                                    border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fafbfc',
                                    overflow: 'hidden',
                                }}>
                                    {/* Zone section header */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        padding: '10px 14px', backgroundColor: '#f1f3f5',
                                        borderBottom: collapsed ? 'none' : '1px solid #e0e0e0',
                                        cursor: 'pointer', userSelect: 'none',
                                    }} onClick={() => toggleCollapse(group.key)}>
                                        <span style={{ fontSize: '12px', color: '#666', width: '14px' }}>
                                            {collapsed ? '▶' : '▼'}
                                        </span>
                                        <span style={{
                                            display: 'inline-block', width: '12px', height: '12px',
                                            borderRadius: '50%', backgroundColor: group.color,
                                            border: '1px solid rgba(0,0,0,0.1)',
                                        }} />
                                        <span style={{ fontWeight: 700, fontSize: '15px' }}>{group.name}</span>
                                        <span style={{ fontSize: '12px', color: '#888' }}>
                                            ({group.rules.length} {group.rules.length === 1 ? 'regel' : 'regler'})
                                        </span>
                                        <button
                                            onClick={e => { e.stopPropagation(); onOpenCreate(group.zoneId); }}
                                            style={{
                                                ...btnStyle('#28a745'),
                                                marginLeft: 'auto', padding: '4px 12px', fontSize: '12px', marginRight: 0,
                                            }}>
                                            + Ny regel
                                        </button>
                                    </div>

                                    {!collapsed && group.rules.length === 0 && (
                                        <div style={{ padding: '14px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
                                            Inga regler i den här zonen än.
                                        </div>
                                    )}

                                    {!collapsed && group.rules.map(rule => {
                                        // While this rule is being edited, render the form in-place instead of
                                        // the read-only card so focus stays put (no jump to the top of the page).
                                        if (editRuleId === rule.id) {
                                            return (
                                                <div key={rule.id} style={{ padding: '14px', borderTop: '1px solid #eee', backgroundColor: 'white' }}>
                                                    {ruleFormPanel}
                                                </div>
                                            );
                                        }
                                        // Resolve display groups: prefer new condition_groups, fall back to flat conditions
                                        const displayGroups: ConditionGroup[] = rule.condition_groups && rule.condition_groups.length > 0
                                            ? rule.condition_groups
                                            : [{
                                                logic: (rule.condition_logic ?? 'AND') as 'AND' | 'OR',
                                                conditions: rule.conditions && rule.conditions.length > 0
                                                    ? rule.conditions
                                                    : (rule.devices ?? []).flatMap(d =>
                                                        (rule.properties ?? (rule.property ? [rule.property] : [])).map(p => ({
                                                            device_ieee: d.ieee_address,
                                                            property: p,
                                                            operator: rule.operator,
                                                            threshold: rule.threshold,
                                                        } as AlertCondition))
                                                      ),
                                            }];
                                        return (
                                            <div key={rule.id} style={{
                                                padding: '14px', opacity: rule.enabled ? 1 : 0.6,
                                                borderLeft: `4px solid ${ruleBorderColor(rule)}`,
                                                borderTop: '1px solid #eee', backgroundColor: 'white',
                                            }}>
                                                {/* Desktop layout */}
                                                <div className="rule-item-desktop" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{rule.name}</span>
                                                        {rule.group_name && rule.group_name.trim() !== '' && (
                                                            <span
                                                                onClick={() => setGroupFilter(rule.group_name!)}
                                                                style={{
                                                                    display: 'inline-block', marginLeft: '8px',
                                                                    padding: '2px 10px', borderRadius: '12px',
                                                                    backgroundColor: '#e7f1ff', color: '#0056b3',
                                                                    border: '1px solid #b6d4fe',
                                                                    fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                                                                }}
                                                                title="Visa bara den här gruppen"
                                                            >
                                                                # {rule.group_name}
                                                            </span>
                                                        )}
                                                        <PausedBadge rule={rule} />
                                                        <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px' }}>
                                                            {displayGroups.map((dg, gi) => (
                                                                <React.Fragment key={gi}>
                                                                    {gi > 0 && (
                                                                        <span style={{
                                                                            display: 'inline-block', margin: '2px 6px',
                                                                            fontSize: '11px', fontWeight: 700, color: '#007bff',
                                                                        }}>
                                                                            {logicLabel(rule.group_logic ?? 'OR')}
                                                                        </span>
                                                                    )}
                                                                    {displayGroups.length > 1 && (
                                                                        <span style={{ color: '#aaa', fontSize: '13px' }}>(</span>
                                                                    )}
                                                                    {dg.conditions.map((cond, ci) => {
                                                                        const dev = devices.find(d => d.id === cond.device_ieee);
                                                                        return (
                                                                            <React.Fragment key={ci}>
                                                                                {ci > 0 && (
                                                                                    <span style={{
                                                                                        display: 'inline-block', margin: '2px 6px',
                                                                                        fontSize: '11px', fontWeight: 700, color: '#0056b3',
                                                                                    }}>
                                                                                        {logicLabel(dg.logic)}
                                                                                    </span>
                                                                                )}
                                                                                <span style={{
                                                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                                                    backgroundColor: '#f0f4ff', padding: '2px 8px',
                                                                                    borderRadius: '4px', fontSize: '12px', border: '1px solid #cce0ff',
                                                                                }}>
                                                                                    <span style={{ fontWeight: 600 }}>{dev?.name ?? cond.device_ieee}</span>
                                                                                    <span style={{ color: '#888' }}>·</span>
                                                                                    <span style={{ color: '#333' }}>{propLabel(cond.property)}</span>
                                                                                    <span style={{ color: '#007bff', fontWeight: 700 }}>{cond.operator}</span>
                                                                                    <strong>{String(cond.threshold)}</strong>
                                                                                </span>
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                    {displayGroups.length > 1 && (
                                                                        <span style={{ color: '#aaa', fontSize: '13px' }}>)</span>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </div>
                                                        <span style={{ color: '#888', fontSize: '12px' }}>⏱ {rule.cooldown_minutes}min cooldown</span>
                                                        {(rule.trigger_threshold ?? 1) > 1 && (
                                                            <span style={{ color: '#888', fontSize: '12px', marginLeft: '10px' }}>
                                                                {rule.trigger_mode === 'cooldown'
                                                                    ? `⏳ ${rule.trigger_threshold! * rule.cooldown_minutes}min sustained`
                                                                    : `×${rule.trigger_threshold} consecutive`
                                                                }
                                                            </span>
                                                        )}
                                                        {(rule.contacts.length > 0 || (rule.groups?.length ?? 0) > 0) && (
                                                            <div style={{ marginTop: '6px', fontSize: '13px', color: '#555' }}>
                                                                Notify: {(rule.groups ?? []).map(g => (
                                                                    <span key={`g${g.id}`} style={{
                                                                        display: 'inline-block', marginRight: '6px',
                                                                        backgroundColor: '#e8f0fe', padding: '1px 8px', borderRadius: '4px',
                                                                    }}>
                                                                        👥 {g.name} ({g.member_count})
                                                                    </span>
                                                                ))}{rule.contacts.map(c => (
                                                                    <span key={c.id} style={{
                                                                        display: 'inline-block', marginRight: '6px',
                                                                        backgroundColor: '#d4edda', padding: '1px 8px', borderRadius: '4px',
                                                                    }}>
                                                                        {c.name}{c.notify_email ? ' 📧' : ''}{c.notify_sms ? ' 📱' : ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                                        <button onClick={() => onToggleRule(rule.id)} style={btnStyle(rule.enabled ? '#ffc107' : '#28a745', rule.enabled ? '#000' : 'white')}>
                                                            {rule.enabled ? 'Stäng av' : 'Slå på'}
                                                        </button>
                                                        <button onClick={() => onOpenEdit(rule)} style={btnStyle('#6c757d')}>Ändra</button>
                                                        <button
                                                            onClick={() => setCopyDialog({
                                                                rule,
                                                                targetZoneId: '',
                                                                targetDeviceIeee: '',
                                                                newName: `${rule.name} (kopia)`,
                                                                submitting: false,
                                                                warning: null,
                                                            })}
                                                            style={btnStyle('#17a2b8')}>Kopiera</button>
                                                        <button onClick={() => onDeleteRule(rule.id)} style={btnStyle('#dc3545')}>Ta bort</button>
                                                    </div>
                                                </div>

                                                {/* Mobile layout */}
                                                <div className="rule-item-mobile">
                                                    <div>
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{rule.name}</span>
                                                            {rule.group_name && rule.group_name.trim() !== '' && (
                                                                <span
                                                                    onClick={() => setGroupFilter(rule.group_name!)}
                                                                    style={{
                                                                        display: 'inline-block', marginLeft: '6px',
                                                                        padding: '2px 8px', borderRadius: '10px',
                                                                        backgroundColor: '#e7f1ff', color: '#0056b3',
                                                                        border: '1px solid #b6d4fe',
                                                                        fontSize: '10px', fontWeight: 600, cursor: 'pointer',
                                                                    }}
                                                                    title="Visa bara den här gruppen"
                                                                >
                                                                    # {rule.group_name}
                                                                </span>
                                                            )}
                                                            <PausedBadge rule={rule} compact />
                                                        </div>
                                                        <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                            {displayGroups.map((dg, gi) => (
                                                                <div key={gi}>
                                                                    {gi > 0 && (
                                                                        <div style={{
                                                                            fontSize: '10px', fontWeight: 700, color: '#007bff',
                                                                            marginBottom: '2px',
                                                                        }}>
                                                                            {logicLabel(rule.group_logic ?? 'OR')}
                                                                        </div>
                                                                    )}
                                                                    {dg.conditions.map((cond, ci) => {
                                                                        const dev = devices.find(d => d.id === cond.device_ieee);
                                                                        return (
                                                                            <div key={ci} style={{ marginBottom: '2px' }}>
                                                                                {ci > 0 && (
                                                                                    <span style={{
                                                                                        fontSize: '10px', fontWeight: 700, color: '#0056b3',
                                                                                        marginRight: '4px',
                                                                                    }}>
                                                                                        {logicLabel(dg.logic)}
                                                                                    </span>
                                                                                )}
                                                                                <span style={{
                                                                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                                                    backgroundColor: '#f0f4ff', padding: '2px 6px',
                                                                                    borderRadius: '4px', fontSize: '11px', border: '1px solid #cce0ff',
                                                                                    flexWrap: 'wrap',
                                                                                }}>
                                                                                    <span style={{ fontWeight: 600 }}>{dev?.name ?? cond.device_ieee}</span>
                                                                                    <span style={{ color: '#333' }}>{propLabel(cond.property)}</span>
                                                                                    <span style={{ color: '#007bff', fontWeight: 700 }}>{cond.operator}</span>
                                                                                    <strong>{String(cond.threshold)}</strong>
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>
                                                            ⏱ {rule.cooldown_minutes}min
                                                            {(rule.trigger_threshold ?? 1) > 1 && (
                                                                <span style={{ marginLeft: '6px' }}>
                                                                    {rule.trigger_mode === 'cooldown'
                                                                        ? ` ⏳ ${rule.trigger_threshold! * rule.cooldown_minutes}min`
                                                                        : ` ×${rule.trigger_threshold}`
                                                                    }
                                                                </span>
                                                            )}
                                                        </div>
                                                        {rule.contacts.length > 0 && (
                                                            <div style={{ marginBottom: '8px', fontSize: '12px' }}>
                                                                <div style={{ color: '#666', fontWeight: 500, marginBottom: '2px' }}>Skickas till:</div>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                    {rule.contacts.map(c => (
                                                                        <span key={c.id} style={{
                                                                            backgroundColor: '#d4edda', padding: '2px 6px', borderRadius: '4px',
                                                                            fontSize: '11px',
                                                                        }}>
                                                                            {c.name}{c.notify_email ? ' 📧' : ''}{c.notify_sms ? ' 📱' : ''}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                                                        <button onClick={() => onToggleRule(rule.id)} style={btnStyle(rule.enabled ? '#ffc107' : '#28a745', rule.enabled ? '#000' : 'white')}>
                                                            {rule.enabled ? 'Stäng av' : 'Slå på'}
                                                        </button>
                                                        <button onClick={() => onOpenEdit(rule)} style={btnStyle('#6c757d')}>Ändra</button>
                                                        <button
                                                            onClick={() => setCopyDialog({
                                                                rule,
                                                                targetZoneId: '',
                                                                targetDeviceIeee: '',
                                                                newName: `${rule.name} (kopia)`,
                                                                submitting: false,
                                                                warning: null,
                                                            })}
                                                            style={btnStyle('#17a2b8')}>Kopiera</button>
                                                        <button onClick={() => onDeleteRule(rule.id)} style={btnStyle('#dc3545')}>Ta bort</button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                        {emptyZones.length > 0 && (
                            <div style={{ border: '1px dashed #ccc', borderRadius: '8px', backgroundColor: '#fafbfc', overflow: 'hidden' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => setOpenEmptySections(prev => { const n = new Set(prev); if (n.has(sectionKey)) n.delete(sectionKey); else n.add(sectionKey); return n; })}>
                                    <span style={{ fontSize: '12px', color: '#666', width: '14px' }}>{showEmpty ? '▼' : '▶'}</span>
                                    <span style={{ fontWeight: 700, fontSize: '14px', color: '#555' }}>Zoner utan regler</span>
                                    <span style={{ fontSize: '12px', color: '#888' }}>({emptyZones.length})</span>
                                    {!showEmpty && (
                                        <span style={{ fontSize: '12px', color: '#999', marginLeft: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {emptyZones.map(g => g.name).join(', ')}
                                        </span>
                                    )}
                                </div>
                                {showEmpty && (
                                    <div style={{ borderTop: '1px solid #e0e0e0', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                                        {emptyZones.map(group => (
                                            <div key={group.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: '1px solid #f0f0f0' }}>
                                                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: group.color, border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
                                                <span style={{ fontSize: '14px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</span>
                                                <button onClick={() => onOpenCreate(group.zoneId)}
                                                    style={{ ...btnStyle('#28a745'), padding: '3px 10px', fontSize: '12px', marginRight: 0 }}>
                                                    + Ny regel
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        </>
                    );
                };

                if (rules.length === 0 && !showRuleForm) {
                    return (
                        <div style={{ ...sectionStyle, textAlign: 'center', color: '#666' }}>
                            Inga larmregler än. Klicka på ”+ Ny regel” för att skapa en.
                        </div>
                    );
                }

                const toggleCollapse = (key: string) => setCollapsedZones(prev => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key); else next.add(key);
                    return next;
                });

                const filterBar = (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px',
                        padding: '10px 12px', backgroundColor: '#f1f3f5', borderRadius: '6px',
                        border: '1px solid #e0e0e0', flexWrap: 'wrap',
                    }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>Visa grupp:</label>
                        <select
                            style={{ ...inputStyle, maxWidth: '260px' }}
                            value={groupFilter}
                            onChange={e => setGroupFilter(e.target.value)}
                        >
                            <option value="">Alla grupper ({rules.length})</option>
                            <option value="__none__">— Ingen grupp — ({rules.filter(r => !r.group_name || r.group_name.trim() === '').length})</option>
                            {allGroups.map(g => (
                                <option key={g} value={g}>{g} ({rules.filter(r => r.group_name === g).length})</option>
                            ))}
                        </select>
                        {groupFilter !== '' && (
                            <button
                                type="button"
                                onClick={() => setGroupFilter('')}
                                style={{ ...btnStyle('#6c757d'), padding: '4px 12px', fontSize: '12px', marginRight: 0 }}>
                                Rensa
                            </button>
                        )}
                        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#666' }}>
                            Visar {filteredRules.length} av {rules.length} {rules.length === 1 ? 'regel' : 'regler'}
                        </span>
                    </div>
                );

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {filterBar}
                        {sections.map(section => (
                            <div key={section.key} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {section.area && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 4px 0' }}>
                                        <span style={{ display: 'inline-block', width: '14px', height: '14px', borderRadius: '3px', backgroundColor: section.area.color, border: '1px solid rgba(0,0,0,0.1)' }} />
                                        <span style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Område</span>
                                        <span style={{ fontWeight: 700, fontSize: '16px' }}>{section.area.name}</span>
                                        <span style={{ fontSize: '12px', color: '#888' }}>({section.ruleCount} {section.ruleCount === 1 ? 'regel' : 'regler'} i {section.groups.length} {section.groups.length === 1 ? 'zon' : 'zoner'})</span>
                                    </div>
                                )}
                                {!section.area && sections.some(x => x.area) && section.groups.some(g => g.zoneId != null) && (
                                    <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: 1, padding: '6px 4px 0' }}>Utan område</div>
                                )}
                                {renderGroups(section.groups, section.key)}
                            </div>
                        ))}
                    </div>
                );
            })()}

            {/* Copy rule dialog */}
            {copyDialog && (() => {
                const dlg = copyDialog;
                const targetZone = zones.find(z => String(z.id) === dlg.targetZoneId);
                const targetDevices = targetZone ? targetZone.devices : [];
                // Original properties referenced by this rule (for warning)
                const sourceGroups = dlg.rule.condition_groups && dlg.rule.condition_groups.length > 0
                    ? dlg.rule.condition_groups
                    : [{ conditions: dlg.rule.conditions ?? [] }];
                const sourceProps = Array.from(new Set(
                    sourceGroups.flatMap(g => (g.conditions ?? []).map(c => c.property)).filter(Boolean)
                ));
                const targetLoading = !!dlg.targetDeviceIeee
                    && isPropertiesLoading(dlg.targetDeviceIeee);
                const targetProps = dlg.targetDeviceIeee && !targetLoading
                    ? new Set(propertiesForDevice(dlg.targetDeviceIeee))
                    : null;
                const missingProps = targetProps
                    ? sourceProps.filter(p => !targetProps.has(p))
                    : [];

                const close = () => setCopyDialog(null);
                const confirm = async () => {
                    if (!dlg.targetZoneId || !dlg.targetDeviceIeee || !dlg.newName.trim()) return;
                    setCopyDialog(d => d ? { ...d, submitting: true } : d);
                    try {
                        await onCopyRule(
                            dlg.rule,
                            Number(dlg.targetZoneId),
                            dlg.targetDeviceIeee,
                            dlg.newName.trim(),
                        );
                        close();
                    } catch {
                        setCopyDialog(d => d ? { ...d, submitting: false } : d);
                    }
                };

                return (
                    <div
                        onClick={close}
                        style={{
                            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                        }}>
                        <div onClick={e => e.stopPropagation()} style={{
                            backgroundColor: 'white', borderRadius: '8px', padding: '24px',
                            maxWidth: '500px', width: '90%', boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
                        }}>
                            <h3 style={{ marginTop: 0 }}>Kopiera regeln: {dlg.rule.name}</h3>
                            <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
                                Den nya regeln får samma villkor, vilotid och mottagare.
                                Alla enheter i villkoren byts ut mot den enhet du väljer här.
                            </p>

                            <div style={{ marginBottom: '14px' }}>
                                <label style={labelStyle}>Zon</label>
                                <select style={inputStyle} value={dlg.targetZoneId}
                                    onChange={e => setCopyDialog(d => d ? { ...d, targetZoneId: e.target.value, targetDeviceIeee: '' } : d)}>
                                    <option value="">— välj zon —</option>
                                    {zones.map(z => <option key={z.id} value={String(z.id)}>{z.name}</option>)}
                                </select>
                            </div>

                            <div style={{ marginBottom: '14px' }}>
                                <label style={labelStyle}>Enhet</label>
                                <select style={inputStyle} value={dlg.targetDeviceIeee}
                                    disabled={!dlg.targetZoneId}
                                    onChange={e => setCopyDialog(d => d ? { ...d, targetDeviceIeee: e.target.value } : d)}>
                                    <option value="">{dlg.targetZoneId ? '— välj enhet —' : '— välj zon först —'}</option>
                                    {targetDevices.map(d => (
                                        <option key={d.ieee_address} value={d.ieee_address}>{d.name || d.ieee_address}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '14px' }}>
                                <label style={labelStyle}>Nytt namn på regeln</label>
                                <input style={inputStyle} value={dlg.newName}
                                    onChange={e => setCopyDialog(d => d ? { ...d, newName: e.target.value } : d)} />
                            </div>

                            {targetLoading && (
                                <div style={{
                                    padding: '6px 12px', marginBottom: '14px',
                                    backgroundColor: '#e7f1ff', border: '1px solid #b6d4fe',
                                    borderRadius: '4px', fontSize: '12px', color: '#084298',
                                }}>
                                    Hämtar enhetens mätvärden…
                                </div>
                            )}

                            {!targetLoading && missingProps.length > 0 && (
                                <div style={{
                                    padding: '8px 12px', marginBottom: '14px',
                                    backgroundColor: '#fff3cd', border: '1px solid #ffeaa7',
                                    borderRadius: '4px', fontSize: '12px', color: '#856404',
                                }}>
                                    ⚠ Enheten verkar inte ha: {missingProps.map(p => propLabel(p)).join(', ')}.
                                    Regeln skapas ändå.
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button onClick={close} style={btnStyle('#6c757d')} disabled={dlg.submitting}>Avbryt</button>
                                <button
                                    onClick={confirm}
                                    style={btnStyle('#17a2b8')}
                                    disabled={!dlg.targetZoneId || !dlg.targetDeviceIeee || !dlg.newName.trim() || dlg.submitting}>
                                    {dlg.submitting ? 'Kopierar…' : 'Kopiera regeln'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default RulesTab;
